import { useLocation, useParams } from "react-router";
import { Link } from "@/components/ui/link";
import {
  useAssetClasses,
  useCostTypes,
  useProductDocumentsOverview,
} from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./products-inner-tabs.i18n";

export function ProductsInnerTabs() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const t = useT(T);

  const { costTypes, isLoading: costTypesLoading } = useCostTypes(id ?? null);
  const { assetClasses, isLoading: assetClassesLoading } = useAssetClasses(id ?? null);
  const { overview: documentsOverview, isLoading: documentsLoading } = useProductDocumentsOverview(
    id ?? null,
  );

  const hideDocuments = !documentsLoading && documentsOverview.documentTypes.length === 0;
  const hideAssetClasses = !assetClassesLoading && assetClasses.length === 0;
  const hideCost = !costTypesLoading && costTypes.length === 0;

  const tabs: { key: keyof typeof T.en_GB; href: string }[] = [
    { key: "general", href: "general" },
    { key: "suitability", href: "suitability" },
    ...(hideDocuments ? [] : [{ key: "documents" as const, href: "documents" }]),
    ...(hideAssetClasses ? [] : [{ key: "assetClasses" as const, href: "asset-classes" }]),
    ...(hideCost ? [] : [{ key: "cost" as const, href: "cost" }]),
  ];

  const basePath = `/settings/portfolios/${id}/products`;

  return (
    <nav className="flex gap-4 border-b mb-4">
      {tabs.map((tab) => {
        const href = `${basePath}/${tab.href}`;
        const isActive = pathname === href || pathname.startsWith(`${href}/`);

        return (
          <Link
            key={tab.href}
            to={href}
            className={cn(
              "pb-2 text-sm font-medium transition-colors relative",
              isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t[tab.key]}
            {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </Link>
        );
      })}
    </nav>
  );
}
